import React, { useEffect, useMemo } from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { Formik, getIn, useFormikContext } from "formik";
import isEmpty from "lodash/isEmpty";
import omitBy from "lodash/omitBy";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  openDispatchingModal,
  setSetting,
  updateConsignmentMega,
} from "actions";

import { MultiButton } from "components/Button";
import { AddPIC, Button, SecondaryButton } from "components/Form";
import { PICField } from "components/Form/Fields/PICField";
import { Input, SelectField } from "components/Form/FormikControls";
import { BodyArea, ErrorArea } from "components/Form/Grid";
import { Row } from "components/Layout";

import {
  DeclarationTypes,
  EXPIRED_DECLARATION_VERSIONS,
} from "constants/documentTypes";
import { ConsignmentModalSection, ModalTypes } from "constants/navigation";
import { SaleyardSaleTypes } from "constants/sale";
import { Settings } from "constants/settings";
import { Species } from "constants/species";
import { colors } from "constants/theme";

import { consignmentOptionFilter } from "lib/filters";
import {
  getEditConsignmentHashRoute,
  openEditConsignmentModal,
} from "lib/navigation";
import { NVDValidator, saleyardAuctionNVDValidator } from "lib/validation";

import {
  getBusinesses,
  getConsignmentById,
  getConsignmentOptionsByDeploymentSaleId,
  getConsignments,
  getCurrentSale,
  getSelectableBranches,
  selectHasNvdUploadByConsignmentIdLookup,
  selectLastAddedConsignment,
} from "selectors";

import { getAgencyByDeploymentSaleId } from "selectors/agencies";
import { getCandidateDeclarationById } from "selectors/candidateDeclarations";

import {
  useHasAddAttachmentWithoutImagePermission,
  useHasSaleyardOrDeploymentPermission,
  useMountEffect,
} from "hooks";

import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";

import {
  applyOcrDeclaration,
  extractOcrDeclaration,
} from "components/AttachmentForm/constants";

import {
  ButtonLink,
  EditButton,
  FormButtonRow,
  Pencil,
  QuestionnaireRow,
  StyledForm,
} from "./Components";
import { NVD_OPTIONS } from "./NVDOptionToggles";
import {
  BC0720,
  C0413,
  C0720,
  E0413,
  E0720,
  G0720,
  S0413,
  S0720,
  G0524,
} from "./NVDVersionForms";
import { HG0324 } from "./NVDVersionForms/HG0324";
import { declarationVersions } from "./speciesDeclarations";
import { CandidateDeclarationSuggestion } from "./CandidateDeclarationSuggestions";

const defaultSheepDeclaration = {
  fed_feed_containing_animal_fats: false,
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  qa_accredited: false,
  treated_for_scabby_mouth: false,
  veterinary_treatment: false,
  consumed_material_in_withholding_period: false,
  additional_information: "",
  post_breeder_tags: null,
};

const defaultCattleDeclaration = {
  treated_with_hgp: false,
  fed_feed_containing_animal_fats: false,
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  fed_byproduct_stockfeeds: false,
  chemical_residue_restrictions: false,
  veterinary_treatment: false,
  consumed_material_in_withholding_period: false,
  spray_drift_risk: false,
  additional_information: "",
};

const defaultEUCattleDeclaration = {
  fed_feed_containing_animal_fats: false,
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  fed_byproduct_stockfeeds: false,
  chemical_residue_restrictions: false,
  veterinary_treatment: false,
  consumed_material_in_withholding_period: false,
  spray_drift_risk: false,
  additional_information: "",
};

const defaultBobbyCalfDeclaration = {
  qa_accredited: false,
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  consumed_material_in_withholding_period: false,
  veterinary_treatment: false,
  veterinary_treatment_milk: false,
  chemical_residue_restrictions: false,
  additional_information: "",
};

// TODO Fully deprecate this after NOV 2025 and use the updated one below
// For now, we need to keep this for the G0720 form
const defaultGoatDeclaration = {
  // Question 1
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  // Question 2
  feedlot_or_treatment: false,
  // Question 3
  veterinary_treatment: false,
  // Question 4
  consumed_material_in_withholding_period: false,
  // Question 5
  fed_feed_containing_animal_fats: false,
  additional_information: "",
};

const defaultUpdatedGoatDeclaration = {
  // Question 1
  owned_since_birth: undefined,
  owned_since_birth_duration: undefined,
  // Question 2
  veterinary_treatment: false,
  // Question 3
  consumed_material_in_withholding_period: false,
  // Question 4
  fed_feed_containing_animal_fats: false,
  additional_information: "",
};

const defaultHarvestedRangelandGoatDeclaration = {
  meets_harvested_rangeland_goat_definition: undefined,
};

// Create an initial state that contains all the default values, this way we dont need to re-initialise the form if the defaults change.
// This will only work well if the defaults are the same for all fields between nvd versions and species and will have to be handled differently
// if for example there was a different default value for 'additional_information' between EU and normal cattle.
const DEFAULT_DECLARATION = {
  ...defaultBobbyCalfDeclaration,
  ...defaultGoatDeclaration,
  ...defaultUpdatedGoatDeclaration,
};

const defaultDeclarations = {
  S0413: {
    VERSION: DeclarationTypes.S0413,
    ...defaultSheepDeclaration,
  },
  S0720: {
    VERSION: DeclarationTypes.S0720,
    ...defaultSheepDeclaration,
  },
  C0413: {
    VERSION: DeclarationTypes.C0413,
    ...defaultCattleDeclaration,
  },
  C0720: {
    VERSION: DeclarationTypes.C0720,
    ...defaultCattleDeclaration,
  },

  E0413: {
    VERSION: DeclarationTypes.E0413,
    ...defaultEUCattleDeclaration,
  },
  E0720: {
    VERSION: DeclarationTypes.E0720,
    ...defaultEUCattleDeclaration,
  },
  BC0720: {
    VERSION: DeclarationTypes.BC0720,
    ...defaultBobbyCalfDeclaration,
  },
  G0720: {
    VERSION: DeclarationTypes.G0720,
    ...defaultGoatDeclaration,
  },
  G0524: {
    VERSION: DeclarationTypes.G0524,
    ...defaultUpdatedGoatDeclaration,
  },
  HG0324: {
    VERSION: DeclarationTypes.HG0324,
    ...defaultHarvestedRangelandGoatDeclaration,
  },
};

const defaultValues = {
  id: null,
  vendor: { branchId: null },
  LPA: "",
  MSA: "",
  NVD: "",
  quantity_NVD: "",
  additional_properties: [],
  declaration: DEFAULT_DECLARATION,
  vendor_property_id: null,
};

export const getValidationSchema = isSaleyardAuction =>
  Yup.lazy(values =>
    Yup.object().shape({
      id: Yup.string().required("Please select a consignment").nullable(),
      NVD: isSaleyardAuction
        ? saleyardAuctionNVDValidator.required("Please enter an NVD number")
        : NVDValidator.required("Please enter an NVD number"),
      vendor_property_id: Yup.string().nullable().typeError("Invalid PIC"),
      declaration: Yup.object().shape({
        owned_since_birth: Yup.string()
          .nullable()
          .test({
            name: "owned-since-birth-test",
            test: v => v !== "undefined",
            message: "This question must be completed",
          }),
        owned_since_birth_duration: Yup.string()
          .nullable()
          .test("Duration required when answer is no", "Required", value => {
            const { owned_since_birth } = values.declaration;
            if (owned_since_birth === NVD_OPTIONS.NO) {
              return value && value !== "undefined";
            }
            return true;
          }),
      }),
    }),
  );

const isNVDFormValid = formikProps => {
  // errors on additional_properties are just for warnings, we keep it in
  // the validation object for convenience, but this makes the isValid
  // wrong for our case.
  const errorsWeCareAbout = omitBy(
    formikProps.errors,
    (value, key) => key === "additional_properties",
  );
  return isEmpty(errorsWeCareAbout);
};

const getDefaultVersion = (speciesId, consignment) => {
  if (
    consignment &&
    consignment.declaration &&
    consignment.declaration.VERSION
  ) {
    return consignment.declaration.VERSION;
  } else {
    return speciesId === Species.CATTLE
      ? DeclarationTypes.C0720
      : DeclarationTypes.S0720;
  }
};

const getAllowedFieldsForDeclarationVersion = version =>
  Object.keys(defaultDeclarations[version]);

const serialiseDeclarationByType = declaration => {
  // When the declaration type is changed, so are the expected fields, but they do still
  // remain in formik values.
  // Before submitting, strip out anything in the declaration that isn't relevant.
  const allowedFields = getAllowedFieldsForDeclarationVersion(
    declaration.VERSION,
  );
  return allowedFields.reduce((dec, field) => {
    dec[field] = declaration[field];
    return dec;
  }, {});
};

function FormComponent({
  candidateDeclarationId,
  deploymentSaleId,
  handleGoBack,
  DocumentSelector,
}) {
  const formikProps = useFormikContext();
  const {
    errors,
    initialValues,
    values,
    setFieldValue,
    touched,
    handleSubmit,
  } = formikProps;
  const initialConsignmentId = getIn(initialValues, "id");
  const consignmentId = getIn(values, "id");
  const vendorPropertyId = getIn(values, "vendor_property_id");
  const nvdSerial = getIn(values, "NVD");
  const nvdHd = getIn(values, "quantity_NVD");

  const hasAddAttachmentWithoutImagePermission =
    useHasAddAttachmentWithoutImagePermission();

  const selectedConsignment =
    useSelector(getConsignmentById(consignmentId)) || {};

  const consignmentsLookup = useSelector(getConsignments);

  const lastAddedConsignment = useSelector(selectLastAddedConsignment);

  const businessesLookup = useSelector(getBusinesses);

  const lastAddedVendorId = lastAddedConsignment?.vendor_id;
  const lastAddedBrands = businessesLookup[lastAddedVendorId]?.brands || "";

  const consignmentOptions = useSelector(
    getConsignmentOptionsByDeploymentSaleId(deploymentSaleId),
  );

  const hasNvdUploadByConsignmentId = useSelector(
    selectHasNvdUploadByConsignmentIdLookup,
  );

  const branches = useSelector(getSelectableBranches);
  const speciesId = useSelector(state => getCurrentSale(state).species_id);
  const agency = useSelector(getAgencyByDeploymentSaleId(deploymentSaleId));

  // Only show if it was already selected or doesn't have an NVD attached
  const filteredConsignmentOptions = consignmentOptions.filter(
    option =>
      initialConsignmentId === option.value ||
      !hasNvdUploadByConsignmentId[option.value],
  );

  const branchSelectOptions = useMemo(
    () =>
      branches.map(branch => ({
        value: branch.id,
        label: branch.name,
      })),
    [branches],
  );

  const submitDisabled =
    formikProps.submitCount > 0 && !isNVDFormValid(formikProps);

  // Warning for missing additional PIC's cannot be done using
  // formik/yup validation because it will stop submission.  So put it in here.
  const additionalPicErrors = [];
  if (
    formikProps.values.declaration?.owned_since_birth === NVD_OPTIONS.NO &&
    formikProps.values.declaration?.VERSION === DeclarationTypes.S0413 &&
    formikProps.values.additional_properties.length === 0
  ) {
    additionalPicErrors.push(
      'One or more additional PICs are required when question 3 is answered "No"',
    );
  }

  const declarationVersion = formikProps.values.declaration.VERSION;

  if (formikProps.status?.addPicFieldWarning) {
    additionalPicErrors.push("One or more additional PICs are invalid.");
  }

  const dispatch = useDispatch();

  //  As we don't have a callback after add on the modal popup, check for
  // a value stashed in settings after hitting create.
  // We want to clear the previous value on load, and after selecting one.
  useMountEffect(() => {
    dispatch(setSetting(Settings.lastAddedConsignmentId, null));
  });
  useEffect(() => {
    dispatch(setSetting(Settings.lastAddedConsignmentId, null));
  }, [values.id, dispatch]);

  useEffect(() => {
    if (!isEmpty(lastAddedConsignment) && !values.id) {
      setFieldValue("id", lastAddedConsignment.id);
      setFieldValue("NVD", lastAddedConsignment.NVD);
      setFieldValue(
        "vendor_property_id",
        lastAddedConsignment.vendor_property_id,
      );
      setFieldValue("brands", lastAddedBrands);
    }
  }, [setFieldValue, values.id, lastAddedConsignment, lastAddedBrands]);

  const onConsignmentSelected = nextConsignmentId => {
    const nextConsignment = consignmentsLookup[nextConsignmentId];

    const touchedFields = Object.entries(touched)
      .filter(
        ([_ignored, objectOrIsTouched]) =>
          typeof objectOrIsTouched === "boolean" && objectOrIsTouched,
      )
      .map(([fieldName]) => fieldName);

    if (!touchedFields.includes("NVD")) {
      setFieldValue("NVD", nextConsignment?.NVD);
    }

    if (!touchedFields.includes("quantity_NVD")) {
      setFieldValue("quantity_NVD", nextConsignment?.quantity_NVD ?? "");
    }

    if (!touchedFields.includes("brands")) {
      setFieldValue(
        "brands",
        businessesLookup[nextConsignment.vendor_id]?.brands,
      );
    }

    // Always update the Vendor PIC as the next business may not have that PIC
    // associated with them and will need to be added/created as a new temp PIC
    setFieldValue(
      "vendor_property_id",
      getIn(nextConsignment, "vendor_property_id"),
    );

    if (!touchedFields.includes("additional_properties")) {
      setFieldValue(
        "additional_properties",
        getIn(nextConsignment, "additional_properties").map(
          property => property.PIC,
        ),
      );
    }

    setFieldValue("declaration", {
      VERSION: getDefaultVersion(speciesId, nextConsignment),
      ...(nextConsignment.declaration || defaultValues.declaration),
    });
  };

  const attachmentButtons = [
    {
      title: "Submit",
      dataTour: "submit",
      onClick: handleSubmit,
      default: true,
    },

    {
      title: "Submit And Create New Consignment",
      onClick: () => {
        handleSubmit();
        if (isEmpty(errors)) {
          openEditConsignmentModal();
        }
      },
      icon: faPlus,
      dataTour: "submitAndCreateNewConsignment",
      iconColor: "black",
    },
  ];

  return (
    <StyledForm>
      <QuestionnaireRow>
        <DocumentSelector />
      </QuestionnaireRow>
      {declarationVersions[speciesId]?.length > 1 && (
        <QuestionnaireRow>
          <SelectField
            name="declaration.VERSION"
            options={declarationVersions[speciesId]}
            label="Version"
            showValueSuggestedValueNotAvailable
          />

          <CandidateDeclarationSuggestion
            candidateDeclarationId={candidateDeclarationId}
            fieldName="declaration.VERSION"
          />
          {EXPIRED_DECLARATION_VERSIONS.includes(declarationVersion) && (
            <ErrorArea large>
              That declaration version is no longer accepted.
              <br />
              <a
                href="https://www.integritysystems.com.au/on-farm-assurance/national-vendor-declaration-nvd/nvd-0720-changes/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here for more information.
              </a>
            </ErrorArea>
          )}
        </QuestionnaireRow>
      )}
      <QuestionnaireRow>
        <Row>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectField
              name="id"
              options={filteredConsignmentOptions}
              label={`Owner / Vendor${agency.name ? ` (${agency.name})` : ""}`}
              onChangeExtra={onConsignmentSelected}
              filterOption={consignmentOptionFilter}
            />

            <CandidateDeclarationSuggestion
              candidateDeclarationId={candidateDeclarationId}
              fieldName="id"
            />
          </div>
          {formikProps.values.id ? (
            <EditButton
              to={`#${getEditConsignmentHashRoute(
                selectedConsignment.id,
                null,
                null,
              )}`}
            />
          ) : (
            <ButtonLink
              as="button"
              type="button"
              onClick={() =>
                dispatch(
                  openDispatchingModal(
                    ModalTypes.Consignment,
                    null,
                    {
                      values: {
                        agency_id: agency.id,
                        vendor_property_id: vendorPropertyId,
                        quantity_NVD: nvdHd,
                        NVD: nvdSerial,
                      },
                    },
                    {
                      consignmentId: null,
                      section: ConsignmentModalSection.GENERAL,
                      showSaleLots: false,
                    },
                  ),
                )
              }
            >
              <Pencil icon={faPlus} />
            </ButtonLink>
          )}
        </Row>
      </QuestionnaireRow>

      {branches.length > 0 && (
        <QuestionnaireRow>
          <SelectField
            name="vendor.branchId"
            options={branchSelectOptions}
            label="Branch"
          />
        </QuestionnaireRow>
      )}
      <QuestionnaireRow>
        <Input name="NVD" label="NVD Number" maxLength={9} />
        <CandidateDeclarationSuggestion
          candidateDeclarationId={candidateDeclarationId}
          fieldName="NVD"
        />
      </QuestionnaireRow>
      <QuestionnaireRow>
        <Input
          name="quantity_NVD"
          label="NVD Hd"
          type="number"
          tooltip="The number of animals as written on the National Vendor Declaration."
        />

        <CandidateDeclarationSuggestion
          candidateDeclarationId={candidateDeclarationId}
          fieldName="quantity_NVD"
        />
      </QuestionnaireRow>
      <QuestionnaireRow>
        <PICField
          name="vendor_property_id"
          label="PIC"
          businessId={selectedConsignment.vendor_id}
          showAllProperties
          preferBusinessProperties
          key={selectedConsignment.vendor_id}
        />

        <CandidateDeclarationSuggestion
          candidateDeclarationId={candidateDeclarationId}
          fieldName="vendor_property_id"
        />
      </QuestionnaireRow>
      <QuestionnaireRow>
        <BodyArea>
          <AddPIC label="additional_properties" formikProps={formikProps} />
        </BodyArea>
        <ErrorArea color={colors.warning}>
          {additionalPicErrors.map((err, idx) => (
            <span key={`pic-err-${idx}`}>
              {err}
              <br />
            </span>
          ))}
        </ErrorArea>
      </QuestionnaireRow>
      <QuestionnaireRow>
        <Input name="brands" label="Brands" />
      </QuestionnaireRow>
      {formikProps.values.declaration.VERSION === DeclarationTypes.C0413 && (
        <C0413 />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.C0720 && (
        <C0720 candidateDeclarationId={candidateDeclarationId} />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.S0413 && (
        <S0413 />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.S0720 && (
        <S0720 candidateDeclarationId={candidateDeclarationId} />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.E0413 && (
        <E0413 />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.E0720 && (
        <E0720 candidateDeclarationId={candidateDeclarationId} />
      )}
      {formikProps.values.declaration.VERSION === DeclarationTypes.BC0720 && (
        <BC0720 candidateDeclarationId={candidateDeclarationId} />
      )}

      {formikProps.values.declaration.VERSION === DeclarationTypes.G0720 && (
        <G0720 candidateDeclarationId={candidateDeclarationId} />
      )}

      {formikProps.values.declaration.VERSION === DeclarationTypes.G0524 && (
        <G0524 candidateDeclarationId={candidateDeclarationId} />
      )}

      {formikProps.values.declaration.VERSION === DeclarationTypes.HG0324 && (
        <HG0324 />
      )}

      <FormButtonRow>
        <div className="flex">
          <div className="flex flex-1">
            <SecondaryButton
              data-tour="back"
              type="button"
              onClick={handleGoBack}
            >
              Back
            </SecondaryButton>
          </div>
          <div className="flex flex-1">
            {hasAddAttachmentWithoutImagePermission ? (
              <MultiButton buttons={attachmentButtons} fullWidth />
            ) : (
              <Button
                data-tour="submit"
                type="submit"
                disabled={submitDisabled}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </FormButtonRow>
    </StyledForm>
  );
}

const DeclarationComponent = ({
  candidateDeclarationId,
  deploymentSaleId,
  documentType,
  DocumentSelector,
  getNextAttachment,
  handleGoBack,
  selectedAttachment,
  setConfirmBrandUpdateDialogProps,
}) => {
  const speciesId = useSelector(state => getCurrentSale(state).species_id);
  const dispatch = useDispatch();
  const saleType = useSelector(state => getCurrentSale(state).sale_type);
  const isSaleyardAuction = SaleyardSaleTypes.includes(saleType);

  const consignmentsLookup = useSelector(getConsignments);
  const businessLookup = useSelector(getBusinesses);

  const consignment = useSelector(
    getConsignmentById(selectedAttachment?.consignment),
  );

  const {
    id,
    vendor_property_id,
    additional_properties = [],
    NVD,
    quantity_NVD,
    brands,
  } = consignment || {};

  // In the case of a brand that is entered on the consignment, vs a prefill available on a vendor:
  // - use a non-empty value if available (already entered) on the consignment
  // - use the default off the vendor if available.
  const vendorBrands = businessLookup?.[consignment?.vendor_id]?.brands || "";

  const consignmentInitialValues = {
    additional_properties: additional_properties.map(property => property.PIC),
    id,
    NVD,
    quantity_NVD: quantity_NVD ?? "",
    vendor_property_id,
    brands: brands || vendorBrands,
  };

  const candidateDeclaration = useSelector(
    getCandidateDeclarationById(candidateDeclarationId),
  );
  const hasOcrPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureOcrCandidateDeclarations,
    SaleyardPermissions.featureOcrCandidateDeclarations,
  );

  const defaultVersion = getDefaultVersion(speciesId, consignment);

  const suggestedDeclaration = extractOcrDeclaration(
    candidateDeclaration,
    hasOcrPermission,
  );

  //   - No OCR, no saved dec: use species-appropriate defaults
  //   - No OCR, saved dec: use saved dec
  //   - OCR, no saved dec: use suggestedDeclaration
  //   - OCR, saved dec: use saved dec, OCR fills gaps
  const initialDeclarationValues = isEmpty(suggestedDeclaration)
    ? consignment?.declaration ||
      (hasOcrPermission ? {} : defaultDeclarations[defaultVersion])
    : applyOcrDeclaration(suggestedDeclaration, consignment?.declaration || {});

  const initialValues = {
    ...defaultValues,
    ...consignmentInitialValues,
    declaration: {
      ...initialDeclarationValues,
      VERSION: defaultVersion,
    },
    selectedAttachment,
    documentType,
  };

  const onSubmit = values => {
    dispatch(
      updateConsignmentMega(
        {
          ...values,
          declaration: {
            ...serialiseDeclarationByType(values.declaration),
          },
        },
        { changeReason: "Updated declaration" },
      ),
    );

    // If the user has entered a brand against this NVD, and it's different to what's stashed on the vendor,
    // offer to update the vendor, too.

    // The values.id is the consignment id being updated - this form isn't about updating vendors on consignments, so
    // the details of the consignment on the state are referenced to pull the vendors details out.
    const vendor = businessLookup[consignmentsLookup[values.id].vendor_id];

    if (values.brands && vendor.brands !== values.brands) {
      if (vendor.brands) {
        setConfirmBrandUpdateDialogProps({
          question: `Would you like to set "${values.brands}" as the default Brand for the Vendor "${vendor.name}"?`,
          message: `You have entered the Brand "${values.brands}".  The Vendor "${vendor.name}" currently has a default brand of "${vendor.brands}".`,
          vendorId: vendor.id,
          brands: values.brands,
        });
      } else {
        setConfirmBrandUpdateDialogProps({
          question: `Would you like to set "${values.brands}" as the default Brand for the Vendor "${vendor.name}"?`,
          message: `You have entered the Brand "${values.brands}".  The Vendor "${vendor.name}" has no current default brand.`,
          vendorId: vendor.id,
          brands: values.brands,
        });
      }
    }
    getNextAttachment();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema(isSaleyardAuction)}
      onSubmit={onSubmit}
    >
      <FormComponent
        candidateDeclarationId={candidateDeclarationId}
        deploymentSaleId={deploymentSaleId}
        handleGoBack={handleGoBack}
        DocumentSelector={DocumentSelector}
      />
    </Formik>
  );
};

export const Declaration = React.memo(DeclarationComponent);
